// src/pages/HierarchyManagement.js

import React, {
  useEffect,
  useState,
} from "react";

import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiUsers,
  FiX,
} from "react-icons/fi";

import {
  FaUserTie,
} from "react-icons/fa";

import Swal from "sweetalert2";

import {
  getData,
  postData,
} from "../../../services/FetchNodeservices";

// =================================================
// GET ADMIN / USER
// =================================================

const adminData =
  JSON.parse(
    localStorage.getItem(
      "ADMIN_INFO"
    )
  ) || null;

const userData =
  JSON.parse(
    localStorage.getItem(
      "USER_INFO"
    )
  ) || null;

const loggedUserId =
  adminData?.admin_id ||
  adminData?.id ||
  userData?.id ||
  userData?.user_id ||
  null;

const isAdmin =
  !!adminData;

export default function HierarchyManagement() {

  // =================================================
  // STATES
  // =================================================

  const [hierarchy,
    setHierarchy] =
    useState([]);

  const [users,
    setUsers] =
    useState([]);

  const [loading,
    setLoading] =
    useState(true);

  const [openModal,
    setOpenModal] =
    useState(false);

  const [editingUser,
    setEditingUser] =
    useState(null);

  const [formData,
    setFormData] =
    useState({

      user_ids: [],

      designation:
        "",

      manager_id:
        "",
    });

  // =================================================
  // LOAD DATA
  // =================================================

  useEffect(() => {

    loadData();

  }, []);

  const loadData =
    async () => {

      try {

        setLoading(true);

        // =============================================
        // ADMIN => FULL HIERARCHY
        // USER => OWN HIERARCHY
        // =============================================

        const hierarchyAPI =
          isAdmin
            ? "hierarchy/all"
            : `hierarchy/all/${loggedUserId}`;

        const [
          hierarchyRes,
          usersRes,
        ] = await Promise.all([

          getData(
            hierarchyAPI
          ),

          getData(
            "hierarchy/users"
          ),
        ]);

        if (
          hierarchyRes?.status
        ) {

          setHierarchy(
            hierarchyRes.data || []
          );
        }

        if (
          usersRes?.status
        ) {

          setUsers(
            usersRes.data || []
          );
        }

      } catch (err) {

        console.log(
          "Hierarchy Error:",
          err
        );

      } finally {

        setLoading(false);
      }
    };

  // =================================================
  // BUILD TREE
  // =================================================

  const buildTree =
    (items) => {

      const map = {};

      const roots = [];

      items.forEach(
        (item) => {

          map[item.userId] = {

            ...item,

            children: [],
          };
        }
      );

      items.forEach(
        (item) => {

          if (

            item.reportingManagerId &&

            map[
              item.reportingManagerId
            ]

          ) {

            map[
              item.reportingManagerId
            ].children.push(

              map[item.userId]
            );

          } else {

            roots.push(
              map[item.userId]
            );
          }
        }
      );

      return roots;
    };

  const treeData =
    buildTree(
      hierarchy
    );

  // =================================================
  // OPEN MODAL
  // =================================================

  const openForm =
    (row = null) => {

      if (row) {

        setEditingUser(
          row
        );

        setFormData({

          user_ids: [
            row.userId,
          ],

          designation:
            row.designation ||
            "",

          manager_id:
            row.reportingManagerId ||
            "",
        });

      } else {

        setEditingUser(
          null
        );

        setFormData({

          user_ids: [],

          designation:
            "",

          manager_id:
            "",
        });
      }

      setOpenModal(true);
    };

  // =================================================
  // SAVE
  // =================================================

  const saveHierarchy =
    async () => {

      try {

        let result;

        if (
          editingUser
        ) {

          result =
            await postData(

              "team_structure/update-member",

              {

                user_id:
                  editingUser.userId,

                designation:
                  formData.designation,

                manager_id:
                  formData.manager_id,
              }
            );

        } else {

          result =
            await postData(

              "team_structure/add-member",

              formData
            );
        }

        if (
          result?.status
        ) {

          Swal.fire({

            icon:
              "success",

            title:
              result.message,
          });

          setOpenModal(
            false
          );

          loadData();

        } else {

          Swal.fire({

            icon:
              "error",

            title:
              result.message,
          });
        }

      } catch {

        Swal.fire({

          icon:
            "error",

          title:
            "Something went wrong",
        });
      }
    };

  // =================================================
  // DELETE
  // =================================================

  const deleteMember =
    async (user_id) => {

      Swal.fire({

        title:
          "Remove Member?",

        icon:
          "warning",

        showCancelButton:
          true,
      }).then(
        async (
          res
        ) => {

          if (
            res.isConfirmed
          ) {

            const result =
              await postData(

                "team_structure/remove-member",

                {
                  user_id,
                }
              );

            if (
              result?.status
            ) {

              Swal.fire({

                icon:
                  "success",

                title:
                  result.message,
              });

              loadData();
            }
          }
        }
      );
    };

  // =================================================
  // TREE
  // =================================================

  const renderTree =
    (nodes) => {

      return (

        <div
          className="
            flex
            justify-center
            gap-10
            flex-wrap
          "
        >

          {nodes.map(
            (node) => (

              <div
                key={
                  node.userId
                }
                className="
                  flex
                  flex-col
                  items-center
                "
              >

                {/* CARD */}

                <div
                  className="
                    bg-white
                    border
                    border-gray-200
                    rounded-3xl
                    p-5
                    min-w-[240px]
                    shadow-sm
                    relative
                    hover:shadow-md
                    transition-all
                  "
                >

                  {/* ICON */}

                  <div
                    className="
                      w-14
                      h-14
                      rounded-2xl
                      bg-black
                      text-white
                      flex
                      items-center
                      justify-center
                      mx-auto
                      mb-4
                    "
                  >
                    <FaUserTie size={20} />
                  </div>

                  {/* INFO */}

                  <div className="text-center">

                    <h2 className="font-semibold text-sm">
                      {node.name}
                    </h2>

                    <p className="text-xs text-gray-500 mt-1">
                      {node.designation}
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      {node.office}
                    </p>
                  </div>

                  {/* ACTIONS */}

                  <div
                    className="
                      flex
                      justify-center
                      gap-2
                      mt-4
                    "
                  >

                    <button
                      onClick={() =>
                        openForm(
                          node
                        )
                      }
                      className="
                        w-9
                        h-9
                        rounded-xl
                        bg-black
                        text-white
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <FiEdit2 size={14} />
                    </button>

                    <button
                      onClick={() =>
                        deleteMember(
                          node.userId
                        )
                      }
                      className="
                        w-9
                        h-9
                        rounded-xl
                        border
                        border-red-200
                        text-red-500
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* CONNECTOR */}

                {node.children &&
                  node.children.length >
                    0 && (

                  <div
                    className="
                      w-[2px]
                      h-10
                      bg-gray-300
                    "
                  />
                )}

                {/* CHILD */}

                {node.children &&
                  node.children.length >
                    0 && (

                  <div
                    className="
                      border-t-2
                      border-gray-300
                      pt-10
                    "
                  >

                    {renderTree(
                      node.children
                    )}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      );
    };

  // =================================================
  // RETURN
  // =================================================

  return (

    <div
      className="
        p-4
        md:p-6
        bg-[#f8f9fb]
        min-h-screen
        md:ml-72
        mt-14
      "
    >

      {/* HEADER */}

      <div className="flex items-center justify-between mb-6">

        <div>

          <h1 className="text-2xl font-semibold">
            Hierarchy Management
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Team hierarchy & reporting structure
          </p>
        </div>

        <button
          onClick={() =>
            openForm()
          }
          className="
            bg-black
            text-white
            px-5
            py-3
            rounded-2xl
            text-sm
            flex
            items-center
            gap-2
          "
        >
          <FiPlus />
          Add Member
        </button>
      </div>

      {/* STATS */}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">

        <div
          className="
            bg-white
            border
            border-gray-200
            rounded-3xl
            p-5
            shadow-sm
          "
        >

          <div
            className="
              w-12
              h-12
              rounded-2xl
              bg-black
              text-white
              flex
              items-center
              justify-center
              mb-4
            "
          >
            <FiUsers />
          </div>

          <h2 className="text-2xl font-semibold">
            {users.length}
          </h2>

          <p className="text-xs text-gray-500 mt-1">
            Total Employees
          </p>
        </div>
      </div>

      {/* TREE */}

      <div
        className="
          bg-[#f8f9fb]
          rounded-3xl
          p-10
          overflow-auto
          min-h-[600px]
        "
      >

        {loading ? (

          <div className="text-center py-10 text-gray-500">
            Loading...
          </div>

        ) : treeData.length ===
          0 ? (

          <div className="text-center py-10 text-gray-500">
            No Hierarchy Found
          </div>

        ) : (

          renderTree(
            treeData
          )
        )}
      </div>

      {/* MODAL */}

      {openModal && (

        <div
          className="
            fixed
            inset-0
            bg-black/50
            z-50
            flex
            items-center
            justify-center
            p-4
          "
        >

          <div
            className="
              bg-white
              w-full
              max-w-lg
              rounded-3xl
              p-6
              relative
            "
          >

            <button
              onClick={() =>
                setOpenModal(
                  false
                )
              }
              className="
                absolute
                top-5
                right-5
                text-gray-500
              "
            >
              <FiX size={22} />
            </button>

            <h2 className="text-xl font-semibold mb-6">

              {editingUser
                ? "Update Member"
                : "Assign Member"}
            </h2>

            {/* USERS */}

            {!editingUser && (

              <div className="mb-4">

                <label className="text-xs text-gray-500">
                  Select Employees
                </label>

                <select
                  multiple
                  className="
                    w-full
                    mt-2
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                    h-40
                  "
                  onChange={(e) =>

                    setFormData({

                      ...formData,

                      user_ids:
                        Array.from(
                          e.target
                            .selectedOptions,
                          (
                            option
                          ) =>
                            option.value
                        ),
                    })
                  }
                >

                  {users.map(
                    (
                      item
                    ) => (

                      <option
                        key={
                          item.id
                        }
                        value={
                          item.id
                        }
                      >
                        {
                          item.first_name
                        }{" "}
                        {
                          item.last_name
                        }
                      </option>
                    )
                  )}
                </select>
              </div>
            )}

            {/* DESIGNATION */}

            <div className="mb-4">

              <label className="text-xs text-gray-500">
                Designation
              </label>

              <input
                value={
                  formData.designation
                }
                onChange={(e) =>
                  setFormData({

                    ...formData,

                    designation:
                      e.target.value,
                  })
                }
                className="
                  w-full
                  mt-2
                  border
                  border-gray-200
                  rounded-2xl
                  px-4
                  py-3
                  text-sm
                "
                placeholder="Enter Designation"
              />
            </div>

            {/* MANAGER */}

            <div>

              <label className="text-xs text-gray-500">
                Reporting Manager
              </label>

              <select
                value={
                  formData.manager_id
                }
                onChange={(e) =>
                  setFormData({

                    ...formData,

                    manager_id:
                      e.target.value,
                  })
                }
                className="
                  w-full
                  mt-2
                  border
                  border-gray-200
                  rounded-2xl
                  px-4
                  py-3
                  text-sm
                "
              >

                <option value="">
                  Top Level
                </option>

                {users.map(
                  (
                    item
                  ) => (

                    <option
                      key={
                        item.id
                      }
                      value={
                        item.id
                      }
                    >
                      {
                        item.first_name
                      }{" "}
                      {
                        item.last_name
                      }
                    </option>
                  )
                )}
              </select>
            </div>

            {/* SAVE */}

            <button
              onClick={
                saveHierarchy
              }
              className="
                mt-6
                w-full
                bg-black
                text-white
                py-3
                rounded-2xl
                font-medium
              "
            >
              {editingUser
                ? "Update Member"
                : "Assign Member"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}